import React from 'react';
import {defineMessages} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule} from '@igp/shared/access-control';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import {RealtimeUpdatesMode} from '@igp/shared/redux/realtime';
import type {ViewType} from '@igp/shared/redux/view';
import {extendedViewCleanDelay} from '@igp/shared/redux/view';

import {groups, withAccessCheck} from '@access-control';
import type {ReferralLinkConfigDataGridColumn} from '@features/referral-link-config';
import {
    AddReferralLinkConfigButton,
    ReferralLinkConfigDataGrid,
    ReferralLinkConfigQueryFilters,
    useReferralLinkConfigs,
} from '@features/referral-link-config';

const localized = defineMessages({
    title: {
        id: 'ReferralLinkConfigPage_title',
        defaultMessage: 'Referral Link Configuration',
    },
});

function ReferralLinkConfigPageInner() {
    const viewType: ViewType = 'ReferralLinkConfig';

    const {
        items,
        rawItems,
        totalCount,
        searchFilter,
        filterString,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        handleFilterChange,
    } = useReferralLinkConfigs({
        viewType,
        fields: ['aal_group_name', 'referral_link_base_url', 'license'],
        defaultPaging: {page: 1, pageSize: 25},
        defaultSorting: [{field: 'aal_group_name', sort: 'asc'}],
        validateFilter: () => true,
        syncWithUrl: true,
        realtimeMode: RealtimeUpdatesMode.Silent,
        cleanDelay: extendedViewCleanDelay,
    });

    const columns: ReferralLinkConfigDataGridColumn[] = ['aal_group_name', 'license', 'referral_link_base_url', 'edit', 'delete'];

    return (
        <LayoutQueue
            header={<PageHeader header={localized.title} actions={<AddReferralLinkConfigButton />} />}
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems>
                            <ReferralLinkConfigQueryFilters model={filterString} onChange={handleFilterChange} availableFilters={['aal']} />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <ReferralLinkConfigDataGrid
                    columns={columns}
                    rows={items}
                    rawModels={rawItems}
                    paging={{
                        page: searchFilter?.paging?.page,
                        pageSize: searchFilter?.paging?.pageSize,
                        rowCount: totalCount,
                    }}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    sortModel={{sortModel: searchFilter?.sorting}}
                    onSortChange={handleSortChange}
                />
            }
        />
    );
}

export const referralLinkConfigPageResource = defineAccessControlledResource({
    name: 'ReferralLinkConfigPageResource',
    rules: new GroupAccessRule([groups.adminsGroup]),
});

const ReferralLinkConfigPageWithAccess = withAccessCheck(ReferralLinkConfigPageInner, referralLinkConfigPageResource);

export const ReferralLinkConfigPage = withTraceErrorBoundary(ReferralLinkConfigPageWithAccess, ErrorModal);
